(() => {
  'use strict';

  const configElement = document.getElementById('alden-widget-localization');
  if (!configElement) return;

  let config;

  try {
    config = JSON.parse(configElement.textContent);
  } catch (error) {
    return;
  }

  const textMap = config.text || {};
  const patterns = (config.patterns || []).map(([source, replacement]) => ({
    expression: new RegExp(source),
    replacement
  }));
  const pathMap = config.paths || {};
  const observedRoots = new WeakSet();
  const observedFrames = new WeakSet();

  const normalizeTechnicalTokens = (value) => value
    .replaceAll('#Farbe_hellblau', '#Color_light blue')
    .replaceAll('#Farbe_hellgrau', '#Color_light gray')
    .replaceAll('#Color_light grey', '#Color_light gray')
    .replaceAll('#Color_Steelblau', '#Color_Steel blue')
    .replaceAll('#color_hellblau', '#color_light blue');

  const replaceText = (root) => {
    if (!root) return;

    const ownerDocument = root.ownerDocument || root;
    const walker = ownerDocument.createTreeWalker(root, NodeFilter.SHOW_TEXT);
    let node = walker.nextNode();

    while (node) {
      const original = node.nodeValue;
      const trimmed = original.trim();
      const pattern = patterns.find(({ expression }) => expression.test(trimmed));
      const replacement = textMap[trimmed] || (pattern ? trimmed.replace(pattern.expression, pattern.replacement) : null);

      if (replacement && replacement !== trimmed) {
        node.nodeValue = original.replace(trimmed, replacement);
      }

      node = walker.nextNode();
    }

    root.querySelectorAll?.('[aria-label], [title], [alt], option').forEach((element) => {
      ['aria-label', 'title', 'alt'].forEach((attribute) => {
        const value = element.getAttribute(attribute);
        if (!value) return;

        const normalizedValue = normalizeTechnicalTokens(value);
        const pattern = patterns.find(({ expression }) => expression.test(normalizedValue));
        const replacement = textMap[normalizedValue]
          || (pattern ? normalizedValue.replace(pattern.expression, pattern.replacement) : normalizedValue);
        if (replacement) element.setAttribute(attribute, replacement);
      });

      if (element.tagName === 'OPTION') {
        const value = element.textContent.trim();
        if (textMap[value]) element.textContent = textMap[value];
      }
    });
  };

  const normalizeLinks = (root) => {
    root.querySelectorAll?.('a[href]').forEach((link) => {
      let url;

      try {
        url = new URL(link.href, window.location.origin);
      } catch (error) {
        return;
      }

      const replacement = pathMap[url.pathname];
      if (!replacement) return;

      url.pathname = replacement;
      link.href = url.toString();
    });
  };

  const localizeRoot = (root) => {
    replaceText(root);
    normalizeLinks(root);
  };

  const discoverEmbeddedContent = (root) => {
    if (!root?.querySelectorAll) return;

    root.querySelectorAll('iframe').forEach(bindFrame);
    root.querySelectorAll('*').forEach((element) => {
      if (element.shadowRoot) observeRoot(element.shadowRoot);
    });
  };

  const observeRoot = (root) => {
    if (!root || observedRoots.has(root)) return;

    observedRoots.add(root);
    localizeRoot(root);
    discoverEmbeddedContent(root);

    const observer = new MutationObserver((mutations) => {
      mutations.forEach((mutation) => {
        if (mutation.type === 'characterData') {
          replaceText(mutation.target.parentElement);
          return;
        }

        mutation.addedNodes.forEach((node) => {
          if (node.nodeType === Node.TEXT_NODE) {
            replaceText(node.parentElement);
            return;
          }

          if (node.nodeType === Node.ELEMENT_NODE) {
            localizeRoot(node);
            discoverEmbeddedContent(node);
          }
        });
      });
    });

    observer.observe(root, { childList: true, characterData: true, subtree: true });
  };

  function bindFrame(frame) {
    const localizeFrame = () => {
      try {
        const frameDocument = frame.contentDocument;
        if (frameDocument?.body) observeRoot(frameDocument.body);
      } catch (error) {
        return;
      }
    };

    localizeFrame();

    if (observedFrames.has(frame)) return;
    observedFrames.add(frame);
    frame.addEventListener('load', localizeFrame);
  }

  const localizeFrames = () => discoverEmbeddedContent(document);

  observeRoot(document.body);
  localizeFrames();

  const frameObserver = new MutationObserver(localizeFrames);
  frameObserver.observe(document.body, { childList: true, subtree: true });

  // Some widgets replace an about:blank iframe's entire document after it is
  // mounted, which detaches the first observer. Re-scan briefly after load so
  // those late-rendered modals are localized as well.
  let remainingFrameScans = 20;
  const frameScanTimer = window.setInterval(() => {
    localizeFrames();
    remainingFrameScans -= 1;
    if (remainingFrameScans === 0) window.clearInterval(frameScanTimer);
  }, 500);
})();
